import {
  getCustomerBusinessExtraDetailsRetrieveQueryKey,
  getCustomerPartialBusinessExtraDetailsRetrieveQueryKey,
  useCustomerBusinessExtraDetailsUpdate,
  useCustomerPartialBusinessExtraDetailsPartialUpdate,
} from "@boundless/bl-api-client/customer/services"
import {
  CustomerBusinessExtraDetailsUpdateBody,
  CustomerPartialBusinessExtraDetailsPartialUpdateBody,
} from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"

import { ButtonBack } from "@/components/buttons/ButtonBack"
import { ButtonNext } from "@/components/buttons/ButtonNext"
import { AutosaveStatus } from "@/components/form-elements/AutosaveStatus"
import { FormInputMask } from "@/components/form-elements/FormInputMask"
import { FormSection } from "@/components/form-elements/FormSection"
import { LoadingButton } from "@/components/ui/button"
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { CompanyFlowContext } from "@/lib/flows/company"
import { useAutosaveDraft } from "@/lib/forms/useAutosaveDraft"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type {
  BusinessAddress,
  BusinessExtraDetails,
  PartialBusinessExtraDetails,
} from "@boundless/bl-api-client/customer/models"
import type { z } from "zod"

type BusinessExtraDetailsFormSchema = z.infer<
  typeof CustomerBusinessExtraDetailsUpdateBody
>

/**
 * Returns initial form data for the business extra details form.
 *
 * Uses a priority cascade to populate form fields:
 * 1. Full business extra details
 * 2. Partial business extra details
 * 3. Empty/default values
 */
function getInitialExtraDetailsFormData(
  businessExtraDetails: BusinessExtraDetails | null | undefined,
  partialBusinessExtraDetails: PartialBusinessExtraDetails | null | undefined,
): BusinessExtraDetailsFormSchema {
  const source = businessExtraDetails || partialBusinessExtraDetails

  if (!source) {
    return {
      ein: "",
      bn: "",
    }
  }

  return {
    ein: source.ein ?? "",
    bn: source.bn ?? "",
  }
}

interface Props {
  companyUuid: string
  businessAddress: BusinessAddress
  businessExtraDetails: BusinessExtraDetails | undefined
  partialBusinessExtraDetails: PartialBusinessExtraDetails | undefined
}

export function BusinessExtraDetailsForm({
  companyUuid,
  businessAddress,
  businessExtraDetails,
  partialBusinessExtraDetails,
}: Props) {
  const queryClient = useQueryClient()

  const flowActorRef = CompanyFlowContext.useActorRef()

  const goBack = () => flowActorRef.send({ type: "back" })
  const goNext = () => flowActorRef.send({ type: "next" })

  const initialValues = getInitialExtraDetailsFormData(
    businessExtraDetails,
    partialBusinessExtraDetails,
  )

  const form = useForm<BusinessExtraDetailsFormSchema>({
    resolver: zodResolver(CustomerBusinessExtraDetailsUpdateBody),
    defaultValues: initialValues,
  })

  const { mutate: update, isPending } = useCustomerBusinessExtraDetailsUpdate({
    mutation: {
      onSuccess: (details: BusinessExtraDetails) => {
        const queryKey =
          getCustomerBusinessExtraDetailsRetrieveQueryKey(companyUuid)
        queryClient.setQueryData(queryKey, details)
        goNext()
      },
      onError: (error: ErrorType<ErrorResponseType>) => {
        applyErrorsToForm(form.setError, error)
      },
    },
  })

  const { mutateAsync: updateDraft } =
    useCustomerPartialBusinessExtraDetailsPartialUpdate({
      mutation: {
        meta: { shouldDisplayErrorToast: false },
        onSuccess: (draft: PartialBusinessExtraDetails) =>
          queryClient.setQueryData(
            getCustomerPartialBusinessExtraDetailsRetrieveQueryKey(companyUuid),
            draft,
          ),
      },
    })

  // Only the field the incorporated country calls for is rendered, so the
  // other one never changes and never reaches the draft.
  const autosave = useAutosaveDraft({
    form,
    initialValues,
    schema: CustomerPartialBusinessExtraDetailsPartialUpdateBody,
    enabled: !businessExtraDetails,
    save: (data) => updateDraft({ companyUuid, data }),
  })

  // Busy while submitting waits out a draft save, so it cannot submit twice.
  const isSubmitting = isPending || form.formState.isSubmitting

  const handleSubmit = async (formData: BusinessExtraDetailsFormSchema) => {
    // Saving the real record deletes the draft. A save already sent would
    // land after that and recreate one for a completed screen.
    await autosave.cancel()
    update({ companyUuid, data: formData })
  }

  const isUS = businessAddress.incorporatedCountry === "US"
  const isCA = businessAddress.incorporatedCountry === "CA"

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle>Business Additional Details</CardTitle>
            <CardAction>
              <AutosaveStatus status={autosave.status} />
            </CardAction>
          </CardHeader>

          <CardContent className="@container/form">
            <FormSection>
              {isUS && (
                <FormInputMask
                  name="ein"
                  label="EIN (Employer Identification Number)"
                  placeholder="EIN (Employer Identification Number)"
                  mask="99-9999999"
                  className="col-span-2"
                />
              )}
              {isCA && (
                <FormInputMask
                  name="bn"
                  label="BN (Federal Business Number)"
                  placeholder="BN (Federal Business Number)"
                  mask="999999999"
                  className="col-span-2"
                />
              )}
            </FormSection>
          </CardContent>

          <CardFooter className="w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <ButtonBack onClick={() => goBack()} className="w-full sm:w-auto" />
            {isSubmitting ? (
              <LoadingButton className="w-full sm:w-auto" />
            ) : (
              <ButtonNext type="submit" className="w-full sm:w-auto" />
            )}
          </CardFooter>
        </Card>
      </form>
    </Form>
  )
}
